import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import {
  Shield, AlertTriangle, Lock, Ban, RefreshCw, Eye, EyeOff,
  Clock, User, MapPin, CheckCircle, XCircle, Activity,
  ChevronDown, ChevronRight, Download, FileText
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';

// ═══════════════════════════════════════════════════════════════════════════════
// INTERFACES
// ═══════════════════════════════════════════════════════════════════════════════

interface DashboardIndicators {
  failed_logins_24h: number;
  active_lockouts: number;
  blocked_ips: number;
  open_incidents: number;
}

interface LatestAudit {
  id: number;
  status: string;
  audit_type: string;
  completed_at: string;
  summary: {
    critical?: number;
    high?: number;
    medium?: number;
    low?: number;
  };
}

interface LoginTrend {
  date: string;
  successful: number;
  failed: number;
}

interface DashboardData {
  indicators: DashboardIndicators;
  latest_audit: LatestAudit | null;
  trends: LoginTrend[];
  generated_at: string;
}

interface LoginAttempt {
  id: number;
  email: string;
  ip_address: string;
  attempted_at: string;
  successful: boolean;
  user_agent: string;
}

interface AttackFinding {
  type: string;
  severity: string;
  description: string;
  target?: string;
  ip_address?: string;
  count?: number;
  accounts_targeted?: number;
  total_attempts?: number;
}

interface SecurityIncident {
  id: number;
  incident_type: string;
  severity: string;
  status: string;
  title: string;
  description: string;
  ip_address: string | null;
  target_email: string | null;
  detected_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  actions_taken: Array<{ action: string; by: string; at: string }>;
}

interface BlockedIP {
  id: number;
  ip_address: string;
  block_type: string;
  reason: string;
  blocked_by: string | null;
  blocked_at: string;
  expires_at: string | null;
  is_active: boolean;
  notes: string;
}

interface AuditLogEntry {
  id: number;
  user: string | null;
  action: string;
  target_model: string;
  target_id: number | null;
  details: Record<string, any>;
  ip_address: string | null;
  timestamp: string;
}

interface SecurityAudit {
  id: number;
  audit_type: string;
  status: string;
  run_id: string;
  commit_sha: string;
  branch: string;
  triggered_by: string;
  completed_at: string;
  duration_seconds: number;
  summary: Record<string, number>;
  finding_count: number;
}

interface AuditFinding {
  tool?: string;
  file?: string;
  line?: number;
  severity: string;
  type?: string;
  message: string;
  confidence?: string;
  vulnerability_id?: string;
}

interface AuditDetails {
  id: number;
  audit_type: string;
  status: string;
  run_id: string;
  commit_sha: string;
  branch: string;
  triggered_by: string;
  started_at: string;
  completed_at: string;
  duration_seconds: number;
  summary: Record<string, number>;
  findings: AuditFinding[];
  raw_output: string;
}

interface ActiveLockout {
  email: string;
  locked_at: string;
  locked_until: string;
  failed_attempts: number;
  reason: string;
  minutes_remaining: number;
}

type TabType = 'overview' | 'attacks' | 'logins' | 'incidents' | 'audits';

// ═══════════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const SecurityDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Dashboard data
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [loginAttempts, setLoginAttempts] = useState<LoginAttempt[]>([]);
  const [attackFindings, setAttackFindings] = useState<AttackFinding[]>([]);
  const [incidents, setIncidents] = useState<SecurityIncident[]>([]);
  const [blockedIPs, setBlockedIPs] = useState<BlockedIP[]>([]);
  const [auditLog, setAuditLog] = useState<AuditLogEntry[]>([]);
  const [securityAudits, setSecurityAudits] = useState<SecurityAudit[]>([]);
  const [activeLockouts, setActiveLockouts] = useState<ActiveLockout[]>([]);

  // Filters
  const [hoursFilter, setHoursFilter] = useState(24);
  const [emailFilter, setEmailFilter] = useState('');
  const [ipFilter, setIpFilter] = useState('');

  // Expanded audit state
  const [expandedAuditId, setExpandedAuditId] = useState<number | null>(null);
  const [auditDetails, setAuditDetails] = useState<AuditDetails | null>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [severityFilter, setSeverityFilter] = useState<string | null>(null);
  const [downloadingPDF, setDownloadingPDF] = useState<number | null>(null);

  // Modal states
  const [showBlockIPModal, setShowBlockIPModal] = useState(false);
  const [blockIPForm, setBlockIPForm] = useState({
    ip_address: '',
    block_type: 'TEMPORARY',
    reason: 'MANUAL',
    duration_hours: 24,
    notes: ''
  });

  // ═══════════════════════════════════════════════════════════════════════════════
  // DATA LOADING
  // ═══════════════════════════════════════════════════════════════════════════════

  const loadDashboard = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/dashboard/');
      setDashboard(response.data);
    } catch (err: any) {
      console.error('Failed to load dashboard:', err);
      setError(err.response?.data?.error || 'Failed to load dashboard');
    }
  }, []);

  const loadLoginAttempts = useCallback(async () => {
    try {
      const params: Record<string, any> = { hours: hoursFilter };
      if (emailFilter) params.email = emailFilter;
      if (ipFilter) params.ip = ipFilter;

      const response = await api.get('/admin/security/login-attempts/', { params });
      setLoginAttempts(response.data.results);
    } catch (err: any) {
      console.error('Failed to load login attempts:', err);
    }
  }, [hoursFilter, emailFilter, ipFilter]);

  const loadAttackDetection = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/detect-attacks/');
      setAttackFindings(response.data.findings);
    } catch (err: any) {
      console.error('Failed to detect attacks:', err);
    }
  }, []);

  const loadIncidents = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/incidents/');
      setIncidents(response.data.results);
    } catch (err: any) {
      console.error('Failed to load incidents:', err);
    }
  }, []);

  const loadBlockedIPs = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/blocked-ips/');
      setBlockedIPs(response.data.results);
    } catch (err: any) {
      console.error('Failed to load blocked IPs:', err);
    }
  }, []);

  const loadAuditLog = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/audit-log/', {
        params: { hours: hoursFilter }
      });
      setAuditLog(response.data.results);
    } catch (err: any) {
      console.error('Failed to load audit log:', err);
    }
  }, [hoursFilter]);

  const loadSecurityAudits = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/audits/');
      setSecurityAudits(response.data.results);
    } catch (err: any) {
      console.error('Failed to load security audits:', err);
    }
  }, []);

  const loadAuditDetails = useCallback(async (auditId: number) => {
    setLoadingDetails(true);
    try {
      const response = await api.get(`/admin/security/audits/${auditId}/`);
      setAuditDetails(response.data);
    } catch (err: any) {
      console.error('Failed to load audit details:', err);
    } finally {
      setLoadingDetails(false);
    }
  }, []);

  const handleToggleAuditExpand = (auditId: number) => {
    if (expandedAuditId === auditId) {
      setExpandedAuditId(null);
      setAuditDetails(null);
      setSeverityFilter(null);
    } else {
      setExpandedAuditId(auditId);
      setSeverityFilter(null);
      loadAuditDetails(auditId);
    }
  };

  // Sort findings by severity (Critical > High > Medium > Low)
  const severityOrder: Record<string, number> = { 'CRITICAL': 0, 'HIGH': 1, 'MEDIUM': 2, 'LOW': 3, 'INFO': 4 };

  const getSortedFindings = (findings: AuditFinding[]) => {
    return [...findings].sort((a, b) => {
      const sevA = (a.severity || 'INFO').toUpperCase();
      const sevB = (b.severity || 'INFO').toUpperCase();
      return (severityOrder[sevA] ?? 5) - (severityOrder[sevB] ?? 5);
    });
  };

  const getFilteredFindings = (findings: AuditFinding[]) => {
    const sorted = getSortedFindings(findings);
    if (!severityFilter) return sorted;
    return sorted.filter(f => (f.severity || '').toUpperCase() === severityFilter.toUpperCase());
  };

  const handleDownloadPDF = async (auditId: number) => {
    setDownloadingPDF(auditId);
    try {
      const response = await api.get(`/admin/security/audits/${auditId}/pdf/`, {
        responseType: 'blob'
      });

      // Check if response is actually a blob/PDF
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        // Server returned JSON error, not PDF
        const text = await response.data.text();
        const error = JSON.parse(text);
        throw new Error(error.error || 'Failed to generate PDF');
      }

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-audit-${auditId}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error('Failed to download PDF:', err);
      // Try to extract error message from blob response
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const errorData = JSON.parse(text);
          alert(`Failed to download PDF: ${errorData.error || 'Unknown error'}`);
        } catch {
          alert(`Failed to download PDF: ${err.response?.status || 'Unknown error'}`);
        }
      } else {
        alert(`Failed to download PDF: ${err.message || err.response?.data?.error || 'Unknown error'}`);
      }
    } finally {
      setDownloadingPDF(null);
    }
  };

  const loadActiveLockouts = useCallback(async () => {
    try {
      const response = await api.get('/admin/security/active-lockouts/');
      setActiveLockouts(response.data.results);
    } catch (err: any) {
      console.error('Failed to load active lockouts:', err);
    }
  }, []);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    setError(null);
    await Promise.all([
      loadDashboard(),
      loadLoginAttempts(),
      loadAttackDetection(),
      loadIncidents(),
      loadBlockedIPs(),
      loadActiveLockouts(),
      loadSecurityAudits(),
    ]);
    setLoading(false);
  }, [loadDashboard, loadLoginAttempts, loadAttackDetection, loadIncidents, loadBlockedIPs, loadActiveLockouts, loadSecurityAudits]);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    if (activeTab === 'logins') {
      loadLoginAttempts();
      loadAuditLog();
    }
  }, [activeTab, loadLoginAttempts, loadAuditLog]);

  // ═══════════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═══════════════════════════════════════════════════════════════════════════════

  const handleBlockIP = async (ip: string, fromModal = false) => {
    try {
      const data = fromModal ? blockIPForm : {
        ip_address: ip,
        block_type: 'TEMPORARY',
        reason: 'MANUAL',
        duration_hours: 24,
        notes: ''
      };

      await api.post('/admin/security/block-ip/', data);
      await loadBlockedIPs();
      setShowBlockIPModal(false);
      setBlockIPForm({
        ip_address: '',
        block_type: 'TEMPORARY',
        reason: 'MANUAL',
        duration_hours: 24,
        notes: ''
      });
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to block IP');
    }
  };

  const handleUnblockIP = async (ip: string) => {
    if (!window.confirm(`Unblock IP ${ip}?`)) return;
    try {
      await api.post(`/admin/security/unblock-ip/${ip}/`);
      await loadBlockedIPs();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to unblock IP');
    }
  };

  const handleUnlockAccount = async (email: string) => {
    if (!window.confirm(`Unlock account ${email}?`)) return;
    try {
      await api.post('/admin/security/unlock-account/', { email });
      await loadActiveLockouts();
      await loadDashboard();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to unlock account');
    }
  };

  const handleDisableAccount = async (email: string) => {
    const reason = window.prompt(`Reason for disabling ${email}:`);
    if (!reason) return;
    try {
      await api.post('/admin/security/disable-account/', { email, reason });
      alert(`Account ${email} has been disabled`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to disable account');
    }
  };

  const handleForcePasswordReset = async (email: string) => {
    if (!window.confirm(`Force password reset for ${email}? They will need to use the forgot password flow.`)) return;
    try {
      await api.post('/admin/security/force-password-reset/', { email });
      alert(`Password invalidated for ${email}`);
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to force password reset');
    }
  };

  const handleUpdateIncidentStatus = async (incidentId: number, newStatus: string) => {
    try {
      await api.post(`/admin/security/incidents/${incidentId}/update/`, { status: newStatus });
      await loadIncidents();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update incident');
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════════════════

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  const getSeverityColor = (severity: string) => {
    switch (severity.toUpperCase()) {
      case 'CRITICAL': return '#dc2626';
      case 'HIGH': return '#ea580c';
      case 'MEDIUM': return '#ca8a04';
      case 'LOW': return '#16a34a';
      default: return '#6b7280';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toUpperCase()) {
      case 'PASSED': return '#16a34a';
      case 'WARNING': return '#ca8a04';
      case 'FAILED': return '#dc2626';
      case 'OPEN': return '#dc2626';
      case 'INVESTIGATING': return '#ea580c';
      case 'MITIGATED': return '#ca8a04';
      case 'RESOLVED': return '#16a34a';
      default: return '#6b7280';
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════════

  if (loading && !dashboard) {
    return (
      <div className="security-dashboard" style={{ padding: '2rem', textAlign: 'center' }}>
        <RefreshCw className="spin" size={32} />
        <p>Loading security data...</p>
      </div>
    );
  }

  return (
    <div className="security-dashboard" style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <Shield size={28} />
          <h1 style={{ margin: 0, fontSize: '1.5rem' }}>Security Dashboard</h1>
        </div>
        <button
          onClick={loadAllData}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px',
            background: 'white', cursor: 'pointer'
          }}
        >
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', padding: '1rem', borderRadius: '4px', marginBottom: '1rem', color: '#dc2626' }}>
          {error}
        </div>
      )}

      {/* Indicator Cards */}
      {dashboard && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #dc2626' }}>
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Failed Logins (24h)</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: dashboard.indicators.failed_logins_24h > 10 ? '#dc2626' : '#111' }}>
              {dashboard.indicators.failed_logins_24h}
            </div>
          </div>

          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #ea580c' }}>
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Active Lockouts</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: dashboard.indicators.active_lockouts > 0 ? '#ea580c' : '#111' }}>
              {dashboard.indicators.active_lockouts}
            </div>
          </div>

          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #7c3aed' }}>
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Blocked IPs</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>
              {dashboard.indicators.blocked_ips}
            </div>
          </div>

          <div style={{ background: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', borderLeft: '4px solid #0891b2' }}>
            <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Open Incidents</div>
            <div style={{ fontSize: '2rem', fontWeight: 'bold', color: dashboard.indicators.open_incidents > 0 ? '#0891b2' : '#111' }}>
              {dashboard.indicators.open_incidents}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', borderBottom: '1px solid #e5e7eb', paddingBottom: '0.5rem' }}>
        {(['overview', 'attacks', 'logins', 'incidents', 'audits'] as TabType[]).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              padding: '0.5rem 1rem',
              border: 'none',
              background: activeTab === tab ? '#2563eb' : 'transparent',
              color: activeTab === tab ? 'white' : '#374151',
              borderRadius: '4px',
              cursor: 'pointer',
              fontWeight: activeTab === tab ? 'bold' : 'normal',
              textTransform: 'capitalize'
            }}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && dashboard && (
        <div>
          {/* Login Trends Chart */}
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '1.5rem' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Login Trends (7 Days)</h3>
            {dashboard.trends.length > 0 ? (
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dashboard.trends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tickFormatter={(d) => new Date(d).toLocaleDateString()} />
                  <YAxis />
                  <Tooltip labelFormatter={(d) => new Date(d).toLocaleDateString()} />
                  <Legend />
                  <Line type="monotone" dataKey="successful" stroke="#16a34a" name="Successful" />
                  <Line type="monotone" dataKey="failed" stroke="#dc2626" name="Failed" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p style={{ color: '#6b7280', textAlign: 'center' }}>No login data available</p>
            )}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            {/* Latest Audit */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 1rem 0' }}>Latest Security Audit</h3>
              {dashboard.latest_audit ? (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <span style={{
                      padding: '0.25rem 0.75rem',
                      borderRadius: '9999px',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      background: getStatusColor(dashboard.latest_audit.status) + '20',
                      color: getStatusColor(dashboard.latest_audit.status)
                    }}>
                      {dashboard.latest_audit.status}
                    </span>
                    <span style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                      {dashboard.latest_audit.audit_type}
                    </span>
                  </div>
                  <div style={{ color: '#6b7280', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                    {formatDate(dashboard.latest_audit.completed_at)}
                  </div>
                  {dashboard.latest_audit.summary && (
                    <div style={{ display: 'flex', gap: '1rem', fontSize: '0.875rem' }}>
                      <span style={{ color: '#dc2626' }}>Critical: {dashboard.latest_audit.summary.critical || 0}</span>
                      <span style={{ color: '#ea580c' }}>High: {dashboard.latest_audit.summary.high || 0}</span>
                      <span style={{ color: '#ca8a04' }}>Medium: {dashboard.latest_audit.summary.medium || 0}</span>
                      <span style={{ color: '#16a34a' }}>Low: {dashboard.latest_audit.summary.low || 0}</span>
                    </div>
                  )}
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No audits yet. Set up GitHub Actions to enable daily audits.</p>
              )}
            </div>

            {/* Active Lockouts */}
            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
              <h3 style={{ margin: '0 0 1rem 0' }}>Active Lockouts</h3>
              {activeLockouts.length > 0 ? (
                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                  {activeLockouts.map((lockout, idx) => (
                    <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid #e5e7eb' }}>
                      <div>
                        <div style={{ fontWeight: 500 }}>{lockout.email}</div>
                        <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                          {lockout.minutes_remaining} min remaining
                        </div>
                      </div>
                      <button
                        onClick={() => handleUnlockAccount(lockout.email)}
                        style={{ padding: '0.25rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.75rem' }}
                      >
                        Unlock
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ color: '#6b7280' }}>No accounts currently locked</p>
              )}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'attacks' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 style={{ margin: 0 }}>Attack Detection</h3>
            <button
              onClick={loadAttackDetection}
              style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
            >
              Scan Now
            </button>
          </div>

          {attackFindings.length === 0 ? (
            <div style={{ background: '#d1fae5', padding: '2rem', borderRadius: '8px', textAlign: 'center' }}>
              <CheckCircle size={48} color="#16a34a" style={{ marginBottom: '0.5rem' }} />
              <p style={{ color: '#166534', margin: 0, fontWeight: 500 }}>No active threats detected</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {attackFindings.map((finding, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    borderLeft: `4px solid ${getSeverityColor(finding.severity)}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <AlertTriangle size={20} color={getSeverityColor(finding.severity)} />
                        <span style={{ fontWeight: 'bold' }}>{finding.type.replace(/_/g, ' ')}</span>
                        <span style={{
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          background: getSeverityColor(finding.severity) + '20',
                          color: getSeverityColor(finding.severity)
                        }}>
                          {finding.severity}
                        </span>
                      </div>
                      <p style={{ margin: 0, color: '#374151' }}>{finding.description}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      {finding.ip_address && (
                        <button
                          onClick={() => handleBlockIP(finding.ip_address!)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: 'none',
                            borderRadius: '4px',
                            background: '#dc2626',
                            color: 'white',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.25rem'
                          }}
                        >
                          <Ban size={14} /> Block IP
                        </button>
                      )}
                      {finding.target && (
                        <button
                          onClick={() => handleForcePasswordReset(finding.target!)}
                          style={{
                            padding: '0.5rem 1rem',
                            border: '1px solid #ea580c',
                            borderRadius: '4px',
                            background: 'white',
                            color: '#ea580c',
                            cursor: 'pointer'
                          }}
                        >
                          Force Reset
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Blocked IPs */}
          <div style={{ marginTop: '2rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 style={{ margin: 0 }}>Blocked IPs</h3>
              <button
                onClick={() => setShowBlockIPModal(true)}
                style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', background: '#2563eb', color: 'white', cursor: 'pointer' }}
              >
                + Block IP
              </button>
            </div>
            {blockedIPs.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No IPs currently blocked</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden' }}>
                <thead>
                  <tr style={{ background: '#f3f4f6' }}>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>IP Address</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Type</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Reason</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Expires</th>
                    <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {blockedIPs.map(ip => (
                    <tr key={ip.id}>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace' }}>{ip.ip_address}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>{ip.block_type}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>{ip.reason}</td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                        {ip.expires_at ? formatDate(ip.expires_at) : 'Never'}
                      </td>
                      <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                        <button
                          onClick={() => handleUnblockIP(ip.ip_address)}
                          style={{ padding: '0.25rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer', fontSize: '0.75rem' }}
                        >
                          Unblock
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'logins' && (
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
            <select
              value={hoursFilter}
              onChange={(e) => setHoursFilter(Number(e.target.value))}
              style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
            >
              <option value={1}>Last hour</option>
              <option value={6}>Last 6 hours</option>
              <option value={24}>Last 24 hours</option>
              <option value={48}>Last 48 hours</option>
              <option value={168}>Last 7 days</option>
            </select>
            <input
              type="text"
              placeholder="Filter by email..."
              value={emailFilter}
              onChange={(e) => setEmailFilter(e.target.value)}
              style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '200px' }}
            />
            <input
              type="text"
              placeholder="Filter by IP..."
              value={ipFilter}
              onChange={(e) => setIpFilter(e.target.value)}
              style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', minWidth: '150px' }}
            />
            <button
              onClick={loadLoginAttempts}
              style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
            >
              Apply
            </button>
          </div>

          {/* Login Attempts Table */}
          <div style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f3f4f6' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Time</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>IP Address</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Status</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loginAttempts.slice(0, 100).map(attempt => (
                  <tr key={attempt.id}>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>
                      {formatDate(attempt.attempted_at)}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>{attempt.email}</td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: '0.875rem' }}>
                      {attempt.ip_address}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                      {attempt.successful ? (
                        <span style={{ color: '#16a34a', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <CheckCircle size={14} /> Success
                        </span>
                      ) : (
                        <span style={{ color: '#dc2626', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                          <XCircle size={14} /> Failed
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '0.75rem', borderBottom: '1px solid #e5e7eb' }}>
                      {!attempt.successful && (
                        <button
                          onClick={() => handleBlockIP(attempt.ip_address)}
                          style={{ padding: '0.25rem 0.5rem', border: '1px solid #dc2626', borderRadius: '4px', background: 'white', color: '#dc2626', cursor: 'pointer', fontSize: '0.75rem' }}
                        >
                          Block IP
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loginAttempts.length === 0 && (
              <p style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No login attempts found</p>
            )}
          </div>
        </div>
      )}

      {activeTab === 'incidents' && (
        <div>
          <h3 style={{ marginTop: 0 }}>Security Incidents</h3>
          {incidents.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No incidents recorded</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {incidents.map(incident => (
                <div
                  key={incident.id}
                  style={{
                    background: 'white',
                    padding: '1rem',
                    borderRadius: '8px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    borderLeft: `4px solid ${getSeverityColor(incident.severity)}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                        <span style={{ fontWeight: 'bold' }}>{incident.title}</span>
                        <span style={{
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          background: getSeverityColor(incident.severity) + '20',
                          color: getSeverityColor(incident.severity)
                        }}>
                          {incident.severity}
                        </span>
                        <span style={{
                          padding: '0.125rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          background: getStatusColor(incident.status) + '20',
                          color: getStatusColor(incident.status)
                        }}>
                          {incident.status}
                        </span>
                      </div>
                      <p style={{ margin: '0 0 0.5rem 0', color: '#374151', fontSize: '0.875rem' }}>{incident.description}</p>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                        <span>Detected: {formatDate(incident.detected_at)}</span>
                        {incident.ip_address && <span> | IP: {incident.ip_address}</span>}
                        {incident.target_email && <span> | Target: {incident.target_email}</span>}
                      </div>
                    </div>
                    {incident.status !== 'RESOLVED' && (
                      <select
                        value={incident.status}
                        onChange={(e) => handleUpdateIncidentStatus(incident.id, e.target.value)}
                        style={{ padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                      >
                        <option value="OPEN">Open</option>
                        <option value="INVESTIGATING">Investigating</option>
                        <option value="MITIGATED">Mitigated</option>
                        <option value="RESOLVED">Resolved</option>
                        <option value="FALSE_POSITIVE">False Positive</option>
                      </select>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'audits' && (
        <div>
          <h3 style={{ marginTop: 0 }}>Security Audit History</h3>
          {securityAudits.length === 0 ? (
            <div style={{ background: '#f3f4f6', padding: '2rem', borderRadius: '8px', textAlign: 'center' }}>
              <Activity size={48} color="#6b7280" style={{ marginBottom: '0.5rem' }} />
              <p style={{ color: '#6b7280', margin: 0 }}>No audits yet.</p>
              <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                Set up GitHub Actions to run daily security scans.
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {securityAudits.map(audit => (
                <div key={audit.id} style={{ background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  {/* Audit Row Header - Clickable */}
                  <div
                    onClick={() => handleToggleAuditExpand(audit.id)}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '24px 1fr 100px 120px 150px 80px 80px 100px',
                      gap: '0.5rem',
                      padding: '0.75rem 1rem',
                      alignItems: 'center',
                      cursor: 'pointer',
                      background: expandedAuditId === audit.id ? '#f3f4f6' : 'white',
                      borderBottom: expandedAuditId === audit.id ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <div>
                      {expandedAuditId === audit.id ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                    </div>
                    <div style={{ fontSize: '0.875rem' }}>{formatDate(audit.completed_at)}</div>
                    <div style={{ fontSize: '0.875rem' }}>{audit.audit_type}</div>
                    <div>
                      <span style={{
                        padding: '0.25rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        background: getStatusColor(audit.status) + '20',
                        color: getStatusColor(audit.status)
                      }}>
                        {audit.status}
                      </span>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#6b7280' }}>
                      {audit.branch} ({audit.commit_sha?.substring(0, 7)})
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <span style={{
                        padding: '0.125rem 0.5rem',
                        borderRadius: '4px',
                        fontSize: '0.75rem',
                        background: audit.finding_count > 0 ? '#fef3c7' : '#d1fae5',
                        color: audit.finding_count > 0 ? '#92400e' : '#166534'
                      }}>
                        {audit.finding_count}
                      </span>
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>{audit.duration_seconds}s</div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(audit.id); }}
                        disabled={downloadingPDF === audit.id}
                        title="Download PDF Report"
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.25rem',
                          padding: '0.25rem 0.5rem', border: '1px solid #ccc', borderRadius: '4px',
                          background: 'white', cursor: downloadingPDF === audit.id ? 'wait' : 'pointer',
                          fontSize: '0.75rem', opacity: downloadingPDF === audit.id ? 0.7 : 1
                        }}
                      >
                        {downloadingPDF === audit.id ? (
                          <><RefreshCw size={12} className="spin" /> Loading...</>
                        ) : (
                          <><Download size={12} /> PDF</>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedAuditId === audit.id && (
                    <div style={{ padding: '1rem', background: '#fafafa' }}>
                      {loadingDetails ? (
                        <div style={{ textAlign: 'center', padding: '1rem' }}>
                          <RefreshCw className="spin" size={20} />
                          <p style={{ margin: '0.5rem 0 0', color: '#6b7280' }}>Loading details...</p>
                        </div>
                      ) : auditDetails ? (
                        <div>
                          {/* Summary Section - Clickable Filter Badges */}
                          <div style={{ marginBottom: '1rem' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                              <h4 style={{ margin: 0, fontSize: '0.875rem', color: '#374151' }}>Summary</h4>
                              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>(click to filter)</span>
                            </div>
                            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
                              {/* Show All button */}
                              <button
                                onClick={() => setSeverityFilter(null)}
                                style={{
                                  padding: '0.5rem 0.75rem',
                                  background: severityFilter === null ? '#2563eb' : 'white',
                                  color: severityFilter === null ? 'white' : '#374151',
                                  borderRadius: '4px',
                                  border: '1px solid #e5e7eb',
                                  cursor: 'pointer',
                                  fontSize: '0.875rem',
                                  fontWeight: severityFilter === null ? 'bold' : 'normal'
                                }}
                              >
                                All
                              </button>
                              {/* Severity badges in order: Critical, High, Medium, Low */}
                              {['critical', 'high', 'medium', 'low'].map((key) => {
                                const value = (auditDetails.summary || {})[key] || 0;
                                const isActive = severityFilter?.toLowerCase() === key;
                                return (
                                  <button
                                    key={key}
                                    onClick={() => setSeverityFilter(isActive ? null : key.toUpperCase())}
                                    style={{
                                      padding: '0.5rem 0.75rem',
                                      background: isActive ? getSeverityColor(key) : 'white',
                                      color: isActive ? 'white' : getSeverityColor(key),
                                      borderRadius: '4px',
                                      border: `2px solid ${getSeverityColor(key)}`,
                                      cursor: value > 0 ? 'pointer' : 'default',
                                      opacity: value > 0 ? 1 : 0.5,
                                      fontSize: '0.875rem',
                                      fontWeight: 'bold',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '0.25rem'
                                    }}
                                    disabled={value === 0}
                                  >
                                    <span>{value}</span>
                                    <span style={{ fontWeight: 'normal', textTransform: 'capitalize' }}>{key}</span>
                                  </button>
                                );
                              })}
                            </div>
                          </div>

                          {/* Findings Table */}
                          {auditDetails.findings && auditDetails.findings.length > 0 ? (
                            <div>
                              {(() => {
                                const filteredFindings = getFilteredFindings(auditDetails.findings);
                                return (
                                  <>
                                    <h4 style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem', color: '#374151' }}>
                                      Findings ({filteredFindings.length}
                                      {severityFilter && ` of ${auditDetails.findings.length} - ${severityFilter} only`})
                                    </h4>
                                    {filteredFindings.length > 0 ? (
                                      <table style={{ width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '4px', overflow: 'hidden', fontSize: '0.875rem' }}>
                                        <thead>
                                          <tr style={{ background: '#f3f4f6' }}>
                                            <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '70px' }}>Severity</th>
                                            <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '80px' }}>Tool</th>
                                            <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '120px' }}>ID</th>
                                            <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>File</th>
                                            <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb', width: '50px' }}>Line</th>
                                            <th style={{ padding: '0.5rem', textAlign: 'left', borderBottom: '1px solid #e5e7eb' }}>Message</th>
                                          </tr>
                                        </thead>
                                        <tbody>
                                          {filteredFindings.map((finding, idx) => (
                                            <tr key={idx}>
                                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb' }}>
                                                <span style={{
                                                  padding: '0.125rem 0.375rem',
                                                  borderRadius: '4px',
                                                  fontSize: '0.7rem',
                                                  background: getSeverityColor(finding.severity) + '20',
                                                  color: getSeverityColor(finding.severity),
                                                  textTransform: 'uppercase'
                                                }}>
                                                  {finding.severity}
                                                </span>
                                              </td>
                                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontSize: '0.75rem' }}>
                                                {finding.tool || '-'}
                                              </td>
                                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: '0.7rem', color: '#6b7280' }}>
                                                {finding.vulnerability_id || finding.type || '-'}
                                              </td>
                                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                                {finding.file || '-'}
                                              </td>
                                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', textAlign: 'center', fontSize: '0.75rem' }}>
                                                {finding.line || '-'}
                                              </td>
                                              <td style={{ padding: '0.5rem', borderBottom: '1px solid #e5e7eb', fontSize: '0.8rem' }}>
                                                {finding.message}
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    ) : (
                                      <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                                        No {severityFilter} findings.{' '}
                                        <button
                                          onClick={() => setSeverityFilter(null)}
                                          style={{ color: '#2563eb', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                                        >
                                          Show all findings
                                        </button>
                                      </p>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          ) : (
                            <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '4px', textAlign: 'center' }}>
                              <CheckCircle size={24} color="#16a34a" style={{ marginBottom: '0.25rem' }} />
                              <p style={{ margin: 0, color: '#166534', fontSize: '0.875rem' }}>No security findings in this audit</p>
                            </div>
                          )}

                          {/* Audit Info Footer */}
                          <div style={{ marginTop: '1rem', fontSize: '0.75rem', color: '#6b7280', display: 'flex', gap: '1.5rem' }}>
                            <span>Run ID: {auditDetails.run_id}</span>
                            <span>Triggered by: {auditDetails.triggered_by}</span>
                            <span>Started: {formatDate(auditDetails.started_at)}</span>
                          </div>
                        </div>
                      ) : (
                        <p style={{ color: '#6b7280', margin: 0 }}>Failed to load audit details</p>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Block IP Modal */}
      {showBlockIPModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', width: '400px', maxWidth: '90%' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Block IP Address</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>IP Address</label>
                <input
                  type="text"
                  value={blockIPForm.ip_address}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, ip_address: e.target.value })}
                  placeholder="e.g. 192.168.1.1"
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Block Type</label>
                <select
                  value={blockIPForm.block_type}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, block_type: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <option value="TEMPORARY">Temporary</option>
                  <option value="PERMANENT">Permanent</option>
                </select>
              </div>
              {blockIPForm.block_type === 'TEMPORARY' && (
                <div>
                  <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Duration (hours)</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={blockIPForm.duration_hours}
                    onChange={(e) => setBlockIPForm({ ...blockIPForm, duration_hours: e.target.value as any })}
                    onBlur={(e) => {
                      const val = parseInt(e.target.value) || 1;
                      setBlockIPForm({ ...blockIPForm, duration_hours: Math.max(1, val) });
                    }}
                    style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                  />
                </div>
              )}
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Reason</label>
                <select
                  value={blockIPForm.reason}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, reason: e.target.value })}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px' }}
                >
                  <option value="MANUAL">Manual Block</option>
                  <option value="BRUTE_FORCE">Brute Force Attack</option>
                  <option value="CREDENTIAL_STUFFING">Credential Stuffing</option>
                  <option value="BOT_ACTIVITY">Bot Activity</option>
                  <option value="SPAM">Spam/Abuse</option>
                </select>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.25rem', fontSize: '0.875rem' }}>Notes</label>
                <textarea
                  value={blockIPForm.notes}
                  onChange={(e) => setBlockIPForm({ ...blockIPForm, notes: e.target.value })}
                  placeholder="Optional notes..."
                  rows={3}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #ccc', borderRadius: '4px', resize: 'vertical' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setShowBlockIPModal(false)}
                  style={{ padding: '0.5rem 1rem', border: '1px solid #ccc', borderRadius: '4px', background: 'white', cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleBlockIP('', true)}
                  style={{ padding: '0.5rem 1rem', border: 'none', borderRadius: '4px', background: '#dc2626', color: 'white', cursor: 'pointer' }}
                >
                  Block IP
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .spin {
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default SecurityDashboard;
